import * as React from 'react';

import {
  InventoryTransactionType,
  Unit,
} from '@rematter/frontend-lib-api-client';
import { Button, NumberField } from '@rematter/ui-web';
import { cn, humanReadableShortPricingUnit } from '@rematter/ui-web/lib';
import { LucideExternalLink, LucideX } from 'lucide-react';

import { useTranslations } from '../../../i18n/useTranslations';
import {
  ENTITY_PREFIX_MAP,
  parseOriginTransactionItemId,
} from '../../../utils/entityLinks';

import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

type TagMovementAllocationRowProps = {
  originTransactionType: InventoryTransactionType;
  originTransactionItemId: string;
  itemName: string;
  occurredAt: string;
  quantity: number;
  maxQuantity: number;
  quantityUnit: Unit;
  onChange: (quantity: number) => void;
  onRemove: () => void;
  isDisabled?: boolean;
};

export const TagMovementAllocationRow = ({
  originTransactionType,
  originTransactionItemId,
  itemName,
  occurredAt,
  quantity,
  maxQuantity,
  quantityUnit,
  onChange,
  onRemove,
  isDisabled = false,
}: TagMovementAllocationRowProps) => {
  const t = useTranslations();
  const [, setLocation] = useModuleLocation();

  const parsed = parseOriginTransactionItemId(originTransactionItemId);

  const transactionTypeLabel =
    t.movementHistoryView.transactionTypes[originTransactionType] ||
    originTransactionType;

  const handleOriginClick = React.useCallback(() => {
    if (!parsed) return;
    const config = ENTITY_PREFIX_MAP[parsed.prefix];
    if (config) {
      setLocation(config.route(parsed.parentId));
    }
  }, [parsed, setLocation]);

  const formattedDate = React.useMemo(() => {
    if (!occurredAt) return '-';
    const date = new Date(occurredAt.replace(/\[.*\]$/, ''));
    return isNaN(date.getTime())
      ? '-'
      : date.toLocaleString(undefined, {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        });
  }, [occurredAt]);

  const unitLabel = humanReadableShortPricingUnit(quantityUnit);

  const handleChange = React.useCallback(
    (value: number) => {
      const clampedValue = Math.min(Math.max(0, value), maxQuantity);
      onChange(clampedValue);
    },
    [maxQuantity, onChange],
  );

  return (
    <div className="@TagMovementAllocationRow flex items-center gap-3 rounded-md border border-neutral/20 bg-neutral/5 p-3">
      <div className="flex-1">
        <div className="flex items-center gap-2 text-sm font-medium">
          {parsed ? (
            <button
              type="button"
              onClick={handleOriginClick}
              className={cn(
                'group flex items-center gap-1 text-primary hover:underline',
              )}
            >
              <span>
                {transactionTypeLabel} #{parsed.shortId}
              </span>
              <LucideExternalLink className="size-3 opacity-0 transition-opacity group-hover:opacity-100" />
            </button>
          ) : (
            <span>{transactionTypeLabel}</span>
          )}
          <span className="text-neutral/50">•</span>
          <span>{itemName}</span>
        </div>
        <div className="mt-1 text-xs text-neutral/60">{formattedDate}</div>
        <div className="mt-1 text-xs text-neutral/60">
          {t.tagsListView.detail.allocation.maxAvailable(
            maxQuantity,
            unitLabel,
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <NumberField
          aria-label={t.tagsListView.detail.allocation.quantity}
          value={quantity}
          onChange={handleChange}
          minValue={0}
          maxValue={maxQuantity}
          showSteppers={false}
          suffix={unitLabel}
          isDisabled={isDisabled}
          className="w-32"
        />
        <Button
          variant="ghost"
          color="neutral"
          size="icon"
          onPress={onRemove}
          isDisabled={isDisabled}
          aria-label="Remove"
        >
          <LucideX className="size-4" />
        </Button>
      </div>
    </div>
  );
};
